import { type CrossroadsLog, useLogById } from '@/api/hooks/useIntegrationLogs'
import { LogDetailsPanel } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel'
import { CloseOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { Vertical } from '@gravitate-js/excalibrr'
import { Button, Drawer, Spin } from 'antd'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

interface LogDetailsDrawerProps {
  /** The LogGroup id to display. The drawer is open whenever this or `log`
   * is set. */
  logGroupId: string | null
  /** The exact log to show. Callers that already have one in hand (the
   * orders grid carries it on the row) pass it here, so the drawer renders
   * straight away and shows that log rather than guessing at the group's
   * most recent failure. */
  log?: CrossroadsLog | null
  tenantName?: string | null
  onClose: () => void
}

/**
 * Drawer showing one log's details, with a button to deep-link into the full
 * connection logs view for the order. The group is fetched for its metadata
 * (and, absent an explicit `log`, for the most recent failed log in it).
 * Shared by the orders grid and the order details page.
 */
export function LogDetailsDrawer({
  logGroupId,
  log,
  tenantName,
  onClose,
}: LogDetailsDrawerProps) {
  const navigate = useNavigate()
  const { data: logGroup, isLoading } = useLogById(
    logGroupId,
    tenantName ?? undefined,
  )

  // The endpoint returns the whole log group; without a log to show, surface
  // its most recent failed one plus the group metadata for context.
  const selectedLog = useMemo(() => {
    if (log) return log
    if (!logGroup?.logs) return null
    return logGroup.logs.filter((l) => l.status === 'failed').at(-1) ?? null
  }, [log, logGroup])

  const groupMetadata =
    logGroup?.metadata?.type === 'order' ? logGroup.metadata : null

  // Order number used to deep-link into the full connection logs view.
  const logOrderNumber = groupMetadata?.order?.origin_order_number ?? null

  const handleViewAllLogs = useCallback(() => {
    if (!logOrderNumber) return
    navigate(
      `/integration-management/connection-logs?order=${encodeURIComponent(logOrderNumber)}`,
    )
  }, [navigate, logOrderNumber])

  return (
    <Drawer
      title='Log Details'
      placement='right'
      width={500}
      open={!!logGroupId || !!log}
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      extra={
        logOrderNumber ? (
          <Button
            type='link'
            icon={<UnorderedListOutlined />}
            onClick={handleViewAllLogs}
          >
            View all logs
          </Button>
        ) : undefined
      }
    >
      {selectedLog ? (
        <LogDetailsPanel log={selectedLog} groupMetadata={groupMetadata} />
      ) : isLoading ? (
        <Vertical className='h-full items-center justify-center'>
          <Spin />
          <span style={{ marginTop: 12, color: 'var(--text-secondary)' }}>
            Loading log details...
          </span>
        </Vertical>
      ) : (
        <span style={{ color: 'var(--text-secondary)' }}>Log not found</span>
      )}
    </Drawer>
  )
}
